import React, { useState } from "react";
import { useSelector } from "react-redux";

import { NETWORK_LABELS } from "entities/network";
import { validateWalletAddress } from "entities/wallet";
import {
  useGetUserWalletsQuery,
  useCreateWithdrawalRequestMutation,
} from "entities/user";
import {
  CryptoDepositModal,
  CryptoQRModal,
  QUICK_AMOUNTS,
} from "widgets/finance-modal";
import websocketService from "services/websocket";
import type { FinanceModalProps, PaymentMethod } from "widgets/finance-modal";
import type { BalanceType } from "shared/model";
import type { RootState } from "store";
import Modal from "components/Modal";

import "./Finance.css";

export const FinanceModal: React.FC<FinanceModalProps> = ({ onClose }) => {
  const { settings } = useSelector((state: RootState) => state.trading);
  const balanceType: BalanceType = settings.real_balance ? "real" : "demo";
  const [amount, setAmount] = useState<number>(1000);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCryptoModal, setShowCryptoModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("");
  const [selectedNetwork, setSelectedNetwork] = useState<string>("");
  const [selectedWithdrawalCurrency, setSelectedWithdrawalCurrency] =
    useState<string>("");
  const [selectedWithdrawalNetwork, setSelectedWithdrawalNetwork] =
    useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(
    null,
  );
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const [walletAddress, setWalletAddress] = useState<string>("");
  const [withdrawalAddress, setWithdrawalAddress] = useState<string>("");
  const [withdrawalAddressError, setWithdrawalAddressError] =
    useState<string>("");
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const showError = (message: string) => {
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  const {
    data: walletsData,
    isLoading: walletsLoading,
    isError: walletsError,
    error: walletsErrorData,
  } = useGetUserWalletsQuery();

  const [createWithdrawalRequest] = useCreateWithdrawalRequestMutation();

  // Обработка выбора валюты и сети
  const handleCryptoDepositConfirm = async (
    currency: string,
    network: string,
    address: string,
  ) => {
    setSelectedCurrency(currency);
    setSelectedNetwork(network);
    setWalletAddress(address);
    setShowCryptoModal(false);

    // Адрес уже получен из списка кошельков, просто показываем QR
    // QR-код можно сгенерировать на фронте или запросить с бэка
    // Пока генерируем URL для QR-кода через публичный сервис
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(address)}`;
    setQrCodeUrl(qrUrl);
    setShowQRModal(true);
  };

  // Обработка выбора валюты и сети для вывода средств
  const handleCryptoWithdrawalConfirm = async (
    currency: string,
    network: string,
  ) => {
    setSelectedWithdrawalCurrency(currency);
    setSelectedWithdrawalNetwork(network);
    setShowCryptoModal(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (amount <= 0) {
      showError("Amount must be positive");
      return;
    }

    if (amount > 100000) {
      showError("Maximum deposit amount is $100,000");
      return;
    }

    setIsSubmitting(true);

    if (isWithdrawal) {
      try {
        const result = await createWithdrawalRequest({
          amount: String(amount),
          currency: `${selectedWithdrawalCurrency}/${selectedWithdrawalNetwork}`,
          destination_address: withdrawalAddress,
        });
        if ("error" in result) {
          const errData =
            result.error &&
            "data" in result.error &&
            result.error.data &&
            typeof result.error.data === "object"
              ? (result.error.data as Record<string, unknown>)
              : null;
          const backendMessage =
            typeof errData?.message === "string"
              ? errData.message
              : typeof errData?.detail === "string"
                ? errData.detail
                : null;
          showError(
            backendMessage ?? "Withdrawal request failed. Please try again.",
          );
          return;
        }
        setShowSuccessModal(true);
      } catch (error) {
        showError("Withdrawal request failed. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Deposit flow (логика без изменений)
    try {
      if (!paymentMethod) return;

      if (
        paymentMethod === "crypto" &&
        (!selectedCurrency || !selectedNetwork)
      ) {
        setShowCryptoModal(true);
        return;
      }

      const timestamp = Date.now();
      const depositRequest: any = {
        balance_type: balanceType,
        amount: amount,
        payment_method: paymentMethod,
        timestamp: timestamp,
      };

      if (paymentMethod === "crypto") {
        depositRequest.currency = selectedCurrency;
        depositRequest.network = selectedNetwork;
      }

      websocketService.sendDepositRequest(depositRequest);
      onClose();
    } catch (error) {
      showError("Failed to send deposit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickAmount = (quickAmount: number) => {
    setAmount(quickAmount);
  };

  const handleWithdrawalAddressChange = (value: string) => {
    setWithdrawalAddress(value);
    setWithdrawalAddressError(
      validateWalletAddress(selectedWithdrawalNetwork, value),
    );
  };

  return (
    <div className="deposit">
      <form className="deposit-form" onSubmit={handleSubmit}>
        <div className="deposit-form-header">
          <button
            type="button"
            className={`deposit-modal-btn${isWithdrawal ? "" : " active"}`}
            onClick={() => setIsWithdrawal(false)}
          >
            <p className="deposit-form-title">
              {paymentMethod === "crypto" && selectedCurrency && selectedNetwork
                ? `Deposit - ${selectedCurrency.toUpperCase()} (${selectedNetwork.toUpperCase()})`
                : paymentMethod === "card"
                  ? "Deposit - Card"
                  : "Deposit Funds"}
            </p>
          </button>

          <button
            type="button"
            className={`withdraw-modal-btn${isWithdrawal ? " active" : ""}`}
            onClick={() => setIsWithdrawal(true)}
          >
            Withdraw Funds
          </button>
        </div>

        {/* Выбор метода оплаты */}
        <div className="form-group">
          <label>Payment Method:</label>
          <div className="payment-method-selector">
            {/* <button
                type="button"
                className={`payment-method-button${paymentMethod === 'card' ? ' active' : ''}`}
                onClick={() => {
                  setPaymentMethod('card');
                  setSelectedCurrency('');
                  setSelectedNetwork('');
                }}
              >
                💳 Card
              </button> */}
            <button
              type="button"
              className={`payment-method-button${paymentMethod === "crypto" ? " active" : ""}`}
              onClick={() => setPaymentMethod("crypto")}
            >
              ₿ Crypto
            </button>
          </div>
        </div>

        {/* Показываем кнопку выбора криптовалюты, если выбран метод crypto */}
        <div
          className="finance-animated-section"
          style={{
            gridTemplateRows: paymentMethod === "crypto" ? "1fr" : "0fr",
            opacity: paymentMethod === "crypto" ? 1 : 0,
          }}
        >
          <div className="finance-animated-inner">
            <div className="form-group">
              {isWithdrawal ? (
                <>
                  <label>Network:</label>
                  <button
                    type="button"
                    className="select-crypto-button"
                    onClick={() => setShowCryptoModal(true)}
                  >
                    {selectedWithdrawalNetwork
                      ? NETWORK_LABELS[selectedWithdrawalNetwork] ||
                        selectedWithdrawalNetwork
                      : "Select Network"}
                  </button>
                </>
              ) : (
                <>
                  <label>Cryptocurrency:</label>
                  <button
                    type="button"
                    className="select-crypto-button"
                    onClick={() => setShowCryptoModal(true)}
                  >
                    {selectedCurrency && selectedNetwork
                      ? `${selectedCurrency.toUpperCase()} - ${selectedNetwork.toUpperCase()}`
                      : "Select Cryptocurrency & Network"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Amount ($):</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Number(e.target.value))}
            min="1"
            max="100000"
            step="1"
            className="amount-input"
            required
          />
        </div>

        <div className="finance-mode-container">
          <div
            className="finance-animated-section"
            style={{
              gridTemplateRows: isWithdrawal ? "1fr" : "0fr",
              opacity: isWithdrawal ? 1 : 0,
            }}
          >
            <div className="finance-animated-inner">
              <div className="withdrawal-warning">
                <div className="form-group">
                  <label>Wallet Address:</label>
                  <input
                    type="text"
                    value={withdrawalAddress}
                    onChange={(e) =>
                      handleWithdrawalAddressChange(e.target.value)
                    }
                    placeholder="Enter your wallet address"
                    className={`amount-input${withdrawalAddressError ? " input-error" : ""}`}
                  />
                  <span
                    className="field-error"
                    style={{
                      visibility: withdrawalAddressError ? "visible" : "hidden",
                    }}
                  >
                    {withdrawalAddressError || " "}
                  </span>
                </div>
                {walletsLoading && <p>Loading wallet information...</p>}
                {walletsData && walletsData.wallets.length === 0 && (
                  <p>No withdrawal wallets found. Please contact support.</p>
                )}
                {walletsError && (
                  <p>
                    Error loading wallet information:{" "}
                    {walletsErrorData &&
                      "status" in walletsErrorData &&
                      (walletsErrorData.status === 500 ||
                        walletsErrorData.status === 503) &&
                      "Couldn't prepare wallets. Please try again later."}
                    {walletsErrorData &&
                      "status" in walletsErrorData &&
                      walletsErrorData.status === 401 &&
                      "Authorization required. JWT is missing, invalid, or expired"}
                    {walletsErrorData &&
                      !("status" in walletsErrorData) &&
                      "Unknown error occurred while loading wallets. Please try again later."}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div
            className="finance-animated-section"
            style={{
              gridTemplateRows: isWithdrawal ? "0fr" : "1fr",
              opacity: isWithdrawal ? 0 : 1,
            }}
          >
            <div className="finance-animated-inner">
              <div className="form-group">
                <label>Quick Amounts ($):</label>
                <div className="quick-amounts">
                  {QUICK_AMOUNTS.map((quickAmount) => (
                    <button
                      key={quickAmount}
                      type="button"
                      className={`button${amount === quickAmount ? " active" : ""}`}
                      onClick={() => handleQuickAmount(quickAmount)}
                    >
                      <span className="value">
                        {new Intl.NumberFormat("ru-RU").format(quickAmount)}
                      </span>
                      <span className="unit">$</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className={`deposit-form__button cancel${!isWithdrawal ? " cancel_red" : ""}`}
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className={`deposit-form__button deposit${!isWithdrawal ? " deposit_green" : ""}`}
            disabled={
              isSubmitting ||
              amount <= 0 ||
              (isWithdrawal
                ? !paymentMethod ||
                  !selectedWithdrawalNetwork ||
                  !withdrawalAddress ||
                  !!withdrawalAddressError
                : !paymentMethod ||
                  (paymentMethod === "crypto" &&
                    (!selectedCurrency || !selectedNetwork)))
            }
          >
            {!isSubmitting && isWithdrawal && "Withdraw"}
            {!isSubmitting && !isWithdrawal && "Deposit"}
            {isSubmitting && "Request..."}
          </button>
        </div>
      </form>

      {/* Модалка выбора криптовалюты */}
      {showCryptoModal && (
        <CryptoDepositModal
          isOpen={showCryptoModal}
          onClose={() => setShowCryptoModal(false)}
          onConfirm={
            isWithdrawal
              ? handleCryptoWithdrawalConfirm
              : handleCryptoDepositConfirm
          }
          wallets={walletsData?.wallets ?? []}
          isLoading={walletsLoading}
        />
      )}

      {/* Модалка успешной заявки на вывод */}
      <Modal
        isOpen={showSuccessModal}
        onClose={onClose}
        title="Withdrawal Request Submitted"
      >
        <p style={{ padding: "16px 0" }}>
          Your withdrawal request has been submitted successfully. It will be
          processed within 1–3 business days.
        </p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            className="deposit-form__button deposit deposit_green"
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Модалка ошибки */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        autoCloseMs={3000}
      >
        <p style={{ padding: "16px 0" }}>{errorMessage}</p>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            className="deposit-form__button cancel"
            onClick={() => setShowErrorModal(false)}
          >
            OK
          </button>
        </div>
      </Modal>

      {/* Модалка QR кода */}
      {showQRModal && (
        <CryptoQRModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          currency={selectedCurrency}
          network={selectedNetwork}
          amount={amount}
          qrCodeUrl={qrCodeUrl}
          walletAddress={walletAddress}
        />
      )}
    </div>
  );
};
